import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WaitlistForm from "@/components/WaitlistForm";
import { useDocumentMeta } from "@/lib/useDocumentMeta";
import { trackEvent } from "@/lib/analytics";
import { useDownload } from "@/lib/downloads";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ArrowRight,
  Check,
  Download,
  LayoutGrid,
  Terminal,
  Pencil,
  Focus,
  Send,
  Palette,
  Settings2,
  Save,
  Megaphone,
  Boxes,
  AtSign,
  Zap,
  Server,
  Layers,
  GitBranch,
  Search,
  Bell,
  Activity,
  Plug,
  Eye,
  Cpu,
  Gauge,
  Maximize2,
  ClipboardCopy,
  StretchHorizontal,
  Keyboard,
  History,
  Shrink,
  Smartphone,
  ListChecks,
  Split,
} from "lucide-react";
import { Link } from "react-router-dom";

const FREE_FEATURES = [
  {
    icon: LayoutGrid,
    title: "Multi-terminal grid",
    body: "1×1, 2×1, 2×2, 3×2, 3×3 layouts. Add or remove panes anytime without losing your place.",
  },
  {
    icon: Terminal,
    title: "Real terminal emulation",
    body: "Full PTY with your default shell (bash / zsh), xterm-256color, 5,000-line scrollback.",
  },
  {
    icon: Pencil,
    title: "Rename panes",
    body: "Click any pane title to edit. Labels become handles for everything else.",
  },
  {
    icon: Focus,
    title: "Focus highlight",
    body: "The focused pane gets an accent outline so you always know where your typing is going.",
  },
  {
    icon: Send,
    title: "Compose bar",
    body: "A single text area above the grid. Routes to the focused pane on send.",
  },
  {
    icon: Palette,
    title: "Theme picker",
    body: "Appearance ▾ dropdown. Default and Nord ship in-box. No flash on cold boot.",
  },
  {
    icon: Settings2,
    title: "Per-cell controls",
    body: "Clear, refit, close — exactly where you expect them, in the pane header.",
  },
  {
    icon: Save,
    title: "Session restore",
    body: "Layout and per-pane working directories are restored across launches.",
  },
  {
    icon: Activity,
    title: "Live agent status",
    body: "Per-pane state — working, awaiting input, awaiting permission, stalled — pushed straight from your agent's lifecycle hooks. Header dots and a triage strip show who needs you. No terminal scraping.",
  },
  {
    icon: Plug,
    title: "Works with Claude Code, Codex & opencode",
    body: "Status capture runs across all three harnesses through sibling adapters. The dots, triage strip, and fleet tools behave identically no matter which agent is in a pane.",
  },
  {
    icon: Eye,
    title: "Sentinel",
    body: "An opt-in watchdog that watches pane output, summarizes what each agent is doing, and raises safety alerts. Observes, never acts. Deterministic safety rules keep firing even with no model configured.",
  },
  {
    icon: Cpu,
    title: "Sentinel model backends",
    body: "Point it at a local OpenAI-compatible model — Ollama, LM Studio, anything that speaks the API — or bring your own API key. Pick the cost and quality tradeoff that fits.",
  },
  {
    icon: Gauge,
    title: "Context window meter",
    body: "Each pane header shows live token usage — 82k ctx — read from the Claude Code transcript at every turn boundary. Absolute counts, no guesswork.",
  },
  {
    icon: Maximize2,
    title: "Pane zoom",
    body: "Maximize the focused pane to fill its space, toggle back to restore. Per-space and non-destructive. ⌘⇧Z, header button, or palette.",
  },
  {
    icon: ClipboardCopy,
    title: "Copy mode",
    body: "Keyboard-driven scrollback: move, select, and yank without the mouse. Named paste buffers hold what you grabbed.",
  },
  {
    icon: StretchHorizontal,
    title: "Drag to resize",
    body: "Drag any pane border to resize the band. Uniform layout presets snap everything back to even.",
  },
  {
    icon: Keyboard,
    title: "Custom keybindings",
    body: "Remap the shortcuts to your muscle memory. Last-pane toggle jumps you back to where you just were.",
  },
  {
    icon: History,
    title: "Persist spaces across restart",
    body: "Every space — layout, working directories, labels, worktree refs — comes back exactly as you left it on next launch.",
  },
];

const PRO_FEATURES = [
  {
    icon: Boxes,
    title: "Workspace presets",
    accent: "cyan",
    body: "Named, reusable bundles of layout + per-pane cwd + initial prompt + broadcast group. Optional auto-run-on-load. JSON export/import.",
    why: "Your dev stack opens in one click instead of five minutes of cd-ing and starting services. Share a preset with a teammate — your terminal layout becomes versioned, reproducible setup.",
  },
  {
    icon: Zap,
    title: "Macros",
    accent: "cyan",
    body: "{label, command, scope} buttons in each pane header — up to 5 visible + ⋯ overflow. Per-macro focused or broadcast scope. Drag-reorder.",
    why: "Your top 5 commands are one click away in every pane. Restart-dev-server, clear-and-rerun, attach-debugger — turn the muscle-memory you're already using into single-button workflows.",
  },
  {
    icon: Layers,
    title: "Multi-grid spaces",
    accent: "cyan",
    body: "Up to 16 separate grids, each with its own panes, layout, broadcast group, and focus. Switch from the Spaces ▾ dropdown or palette. Load a preset directly into a fresh space.",
    why: "Your work has more than one context. Day job in one space, side project in another, the incident you're triaging in a third — every grid keeps its own state, so the switch is one click instead of thirty windows.",
  },
  {
    icon: Megaphone,
    title: "Broadcast prompts",
    accent: "cyan",
    body: "Pick a group of panes, type once, send to all. Visible orange border on broadcasting panes.",
    why: "Stop typing the same command three times. Apply a git pull, a rebase, or a smoke test across every repo, env, or branch you're juggling — in lockstep.",
  },
  {
    icon: AtSign,
    title: "Cross-pane @mentions",
    accent: "cyan",
    body: "@pane2 inlines the last 200 lines. @pane2:50 overrides line count (1–1000). Handles derive from slugified labels.",
    why: "No more alt-tab copy-paste between terminals. Pipe last night's failing tests into a prompt, or feed a server log straight into your debugger session — inline, in one keystroke.",
  },
  {
    icon: Server,
    title: "MCP Server",
    accent: "cyan",
    body: "Local HTTP server on 127.0.0.1. Exposes list_spaces, list_panes, read_pane, write_pane, search_panes to Claude Desktop, Cursor, and Cline. JSON-RPC + REST. 32-byte bearer token, origin allowlist, per-pane read/write opt-in — writable panes get a red inset border.",
    why: "Your LLM stops being blind to your shells. Claude can read the failing test, Cursor can correlate compile errors with your edits, Cline can run the fix — all without you copy-pasting context, and you can see exactly which panes are exposed.",
  },
  {
    icon: GitBranch,
    title: "Git worktree panes",
    accent: "cyan",
    body: "New worktree pane… spins up a branch as its own pane in one click. Pick the repo, name a branch, the app runs git worktree add and opens a terminal in the new tree. Closing prompts to clean up (with --force retry on dirty state).",
    why: "Review a PR without nuking your in-flight work. Spin up the branch in a fresh pane, poke at it, close the pane to clean the worktree — no stashing, no rebasing back, no second clone of the repo.",
  },
  {
    icon: Search,
    title: "Scrollback capture + cross-pane search",
    accent: "cyan",
    body: "Opt-in per-pane on-disk capture (ANSI-stripped, tail-rotated, 1 MB–200 MB per pane). Cmd+Shift+F searches every pane's live buffer + captures together, with click-to-jump and a live tag when no capture is present.",
    why: "Stop losing the one error you needed because scrollback rolled over. Every pane's history is queryable across the whole grid — find a stack trace from two hours ago and jump straight to the pane that produced it.",
  },
  {
    icon: Bell,
    title: "Attention notifications",
    accent: "cyan",
    body: "OS desktop notifications + dock bounce + optional sound when a background or unfocused pane goes idle, rings the terminal bell, or exits. Per-pane Mute. Space-aware body format.",
    why: "Long-running builds and tests stop being a babysitting job. Send the command, switch to another pane or another space, and get pinged the moment it finishes or breaks — your dock tells you which pane wants attention.",
  },
  {
    icon: Shrink,
    title: "Auto-Compact",
    accent: "cyan",
    body: "Sends /compact to a pane the moment its context crosses your threshold (default 140k), at a clean turn boundary. Panes stay lean; per-turn spend stays down. Builds on the free context meter.",
    why: "Long agent sessions stop bleeding tokens on context you've already paid to carry. Set the ceiling once and every pane trims itself at a safe turn boundary — no watching the meter, no manual /compact mid-thought.",
  },
  {
    icon: Smartphone,
    title: "Webserver",
    accent: "cyan",
    body: "View and drive your panes from a phone or tablet — over your Wi-Fi, or from anywhere through a tunnel like Tailscale or Cloudflare. Configurable port, optional login password (scrypt-hashed). Off by default. Set a password before you expose it.",
    why: "Your grid comes with you. Kick off a long build at your desk, then unblock a permission prompt or fire a prompt from the couch or the train — same panes, no SSH, no laptop open.",
  },
  {
    icon: ListChecks,
    title: "Keep-busy work queue",
    accent: "cyan",
    body: "Queue prompts per pane; when an agent goes idle, the next one feeds in automatically. One per turn, no double-feed. Agents never sit waiting on you.",
    why: "Your agents stop stalling on you. Stack the next prompts per pane and they feed in the moment each one goes idle — the grid keeps moving while you're in a meeting or asleep.",
  },
  {
    icon: Split,
    title: "Worktree fan-out",
    accent: "cyan",
    body: "One task, N isolated git-worktree panes in their own space, each auto-running the same prompt. Partial-failure tolerant. Compare approaches side by side.",
    why: "Stop guessing which approach wins. Fan one task across N isolated worktrees, let them all run the same prompt, and read the diffs side by side — no branch juggling, no clobbered working tree.",
  },
];

export const Landing = () => {
  useDocumentMeta({
    title:
      "Poly Grid — Multi-terminal grid for macOS, a tmux alternative for AI agents",
    description:
      "Run your webservers, build watchers, logs, and AI agents like Claude Code, Codex, and opencode side by side in one tiling grid on macOS. A tmux alternative for multiple terminals and AI agents. Free grid forever; $20 one-time Pro.",
    path: "/",
  });

  const { primary, others, platform } = useDownload();
  const heroLabel = primary.external
    ? "Download"
    : `Download — ${primary.label}`;
  const isNonMac = platform === "linux" || platform === "windows";

  return (
    <div className="relative min-h-screen bg-[var(--pg-bg)]">
      <Navbar />

      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative overflow-hidden border-b border-pg"
      >
        <div className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />
        <div className="absolute inset-x-0 top-0 h-[480px] pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at 70% 0%, rgba(0,229,255,0.10), transparent 60%), radial-gradient(ellipse at 10% 30%, rgba(255,149,0,0.06), transparent 60%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-20 lg:pt-24 lg:pb-28">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <div
                data-testid="hero-tag"
                className="inline-flex items-center gap-2 px-2.5 py-1 border border-pg bg-pg-surface tag-label text-[var(--pg-text-dim)]"
              >
                <span className="w-1.5 h-1.5 bg-[var(--pg-green)] rounded-full" />
                v1.1 — macOS · Apple Silicon + Intel
              </div>
              <h1 className="font-display mt-6 text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter uppercase leading-[0.92]">
                Ship like a
                <br />
                <span className="text-cyan-accent">team of ten.</span>
              </h1>
              <p className="mt-6 text-base lg:text-lg text-[var(--pg-text-dim)] leading-relaxed max-w-lg">
                Run your webservers, build watchers, log tails, and AI agents
                like Claude Code, Codex, and opencode side by side in one tiling
                grid for macOS. Broadcast one prompt to many panes, pipe one
                pane into another, and hand the whole grid to your LLM.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Button
                  asChild
                  data-testid="hero-cta-download"
                  className="h-12 px-6 rounded-sm font-display font-bold tag-label text-black hover:opacity-90"
                  style={{ backgroundColor: "var(--pg-cyan)" }}
                >
                  <a
                    href={primary.url}
                    download={!primary.external}
                    target={primary.external ? "_blank" : undefined}
                    rel={primary.external ? "noopener noreferrer" : undefined}
                    onClick={() =>
                      trackEvent("download_app", {
                        location: "hero",
                        tier: "free",
                        platform: primary.platform,
                      })
                    }
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {heroLabel}
                  </a>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  data-testid="hero-cta-demos"
                  className="h-12 px-6 rounded-sm border-pg bg-transparent hover:bg-pg-surface-2 font-display font-bold tag-label"
                >
                  <a
                    href="#demos"
                    onClick={(e) => {
                      e.preventDefault();
                      trackEvent("see_demos", { location: "hero" });
                      document
                        .getElementById("demos")
                        ?.scrollIntoView({ behavior: "smooth" });
                    }}
                  >
                    See it in action
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </a>
                </Button>
                <Link
                  to="/docs"
                  data-testid="hero-cta-docs"
                  className="font-mono text-sm text-[var(--pg-text-dim)] hover:text-cyan-accent underline-offset-4 hover:underline"
                >
                  Read the docs
                </Link>
              </div>

              <OtherPlatforms others={others} location="hero" />

              <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] font-mono text-[var(--pg-text-muted)]">
                <span className="flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5 text-cyan-accent" /> Free
                  forever
                </span>
                <span className="flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5 text-cyan-accent" /> 7-day Pro
                  trial
                </span>
                <span className="flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5 text-cyan-accent" /> No
                  subscription
                </span>
                <span className="flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5 text-cyan-accent" /> Every
                  v1.x update free
                </span>
              </div>

              <p
                data-testid="hero-free-vs-pro"
                className="mt-6 text-[13px] font-mono text-[var(--pg-text-dim)] leading-relaxed max-w-lg"
              >
                <span className="text-white">Free download:</span> the full
                tiling grid, real shells, themes, and session restore.{" "}
                <span className="text-white">Pro adds</span> broadcast,
                presets, @mentions, macros, and the local MCP server.
              </p>

              <p
                data-testid="hero-origin"
                className="mt-4 text-[12px] font-mono text-[var(--pg-text-muted)] leading-relaxed max-w-lg"
              >
                Built by a developer who got tired of alt-tabbing between
                Claude Code sessions.
              </p>

              {isNonMac && (
                <div
                  data-testid="hero-waitlist"
                  className="mt-6 border border-pg bg-pg-surface p-4 max-w-lg"
                >
                  <p className="text-[13px] font-mono text-[var(--pg-text-dim)] leading-relaxed mb-3">
                    <span className="text-white">
                      On {platform === "windows" ? "Windows" : "Linux"}?
                    </span>{" "}
                    Poly Grid is macOS only today. Get one email when your
                    build lands.
                  </p>
                  <WaitlistForm platform={platform} location="hero" />
                </div>
              )}
            </div>

            <div className="lg:col-span-7 relative">
              <video
                src="/preset-cold-start.mp4"
                poster="/preset-cold-start-poster.jpg"
                preload="metadata"
                autoPlay
                loop
                muted
                playsInline
                aria-label="Poly Grid in action: load a saved workspace and the whole grid spins up, panes and all"
                data-testid="hero-showcase"
                className="w-full h-auto border border-pg shadow-2xl"
              />
              <p className="mt-3 text-[12px] font-mono text-[var(--pg-text-muted)]">
                Pro: loading a saved preset spins up the whole grid, panes,
                working directories, and all.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* DEMOS — See it in action */}
      <section
        id="demos"
        data-testid="demos-section"
        className="border-b border-pg bg-pg-surface/30"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="mb-12">
            <span className="tag-label text-cyan-accent">/// See it in action</span>
            <h2 className="font-display mt-3 text-3xl lg:text-5xl font-bold tracking-tight max-w-3xl">
              Four things that run your fleet.
            </h2>
          </div>

          <div className="space-y-16 lg:space-y-20">
            {[
              {
                tag: "Live fleet status",
                title: "Every agent's state, at a glance.",
                body: "Status hooks light each pane the moment it changes — working, waiting on you, needs permission, done. The triage strip floats whoever needs attention to the front and pings you when an agent stalls or finishes. Run ten agents, supervise like it's one.",
                src: "/fleet.mp4",
                poster: "/fleet-poster.jpg",
                alt: "Live fleet status: panes changing state as agents work, the triage strip surfacing the one that needs you",
              },
              {
                tag: "Sentinel",
                title: "A safety net watching every pane.",
                body: "Sentinel reads what each agent is actually doing — looping, stuck, erroring, waiting — and raises an alert before a runaway turn burns your tokens or your repo. Deterministic rules out of the box, with an optional model backend for summaries and triage.",
                src: "/sentinel.mp4",
                poster: "/sentinel-poster.jpg",
                alt: "Sentinel flagging a stuck agent and surfacing an alert in the triage strip",
              },
              {
                tag: "MCP",
                title: "Claude Desktop and Cursor drive your panes.",
                body: "A local server on 127.0.0.1 exposes your panes to any MCP client — read output, send commands, search, even reshape the grid — for the panes you opt in. Your editor and your fleet, one loop.",
                src: "/mcp.mp4",
                poster: "/mcp-poster.jpg",
                alt: "MCP: an external agent reading and writing a Poly Grid pane over localhost",
              },
              {
                tag: "Webserver",
                title: "Your whole fleet, on your phone.",
                body: "Flip on the LAN webserver and every pane streams to your phone or tablet — watch live output, send a prompt, approve a step from across the room. Password-protected, never leaves your network.",
                src: "/webserver.mp4",
                poster: "/webserver-poster.jpg",
                alt: "Poly Grid webserver: a pane's live output mirrored on a phone, with input sent back",
              },
            ].map((demo) => (
              <div key={demo.tag} className="grid lg:grid-cols-12 gap-8 items-center">
                <div className="lg:col-span-4">
                  <span className="tag-label text-cyan-accent">{demo.tag}</span>
                  <h3 className="font-display mt-2 text-2xl lg:text-3xl font-bold tracking-tight">
                    {demo.title}
                  </h3>
                  <p className="mt-4 text-[var(--pg-text-dim)] text-sm leading-relaxed">
                    {demo.body}
                  </p>
                </div>
                <div className="lg:col-span-8">
                  <video
                    src={demo.src}
                    poster={demo.poster}
                    preload="metadata"
                    autoPlay
                    loop
                    muted
                    playsInline
                    aria-label={demo.alt}
                    className="w-full h-auto border border-pg shadow-2xl"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES — FREE */}
      <section
        id="features"
        data-testid="features-section"
        className="border-b border-pg"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <div className="flex items-center gap-3">
                <span className="tag-label text-cyan-accent">/// Features</span>
                <span className="tag-label text-[var(--pg-text-muted)] border border-pg px-2 py-0.5">
                  Free forever
                </span>
              </div>
              <h2 className="font-display mt-3 text-3xl lg:text-5xl font-bold tracking-tight max-w-2xl">
                The base grid is free.
                <br />
                Forever.
              </h2>
            </div>
            <p className="text-[var(--pg-text-dim)] text-sm max-w-md leading-relaxed">
              Everything you need to run more than one shell at a time. Tile
              the grid, route input through the compose bar, theme to taste,
              and pick up exactly where you left off.
            </p>
          </div>

          <div
            data-testid="free-features-grid"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4"
          >
            {FREE_FEATURES.map((f, i) => (
              <FeatureCell key={i} {...f} />
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES — PRO */}
      <section
        id="pro-features"
        data-testid="pro-features-section"
        className="border-b border-pg bg-[#070707] relative overflow-hidden"
      >
        <div
          className="absolute inset-0 pointer-events-none opacity-60"
          style={{
            background:
              "radial-gradient(ellipse at 80% 0%, rgba(0,229,255,0.07), transparent 55%), radial-gradient(ellipse at 0% 100%, rgba(255,149,0,0.05), transparent 55%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <div className="flex items-center gap-3">
                <span className="tag-label text-cyan-accent">/// Pro</span>
                <span
                  className="tag-label text-black px-2 py-0.5 font-bold"
                  style={{ backgroundColor: "var(--pg-cyan)" }}
                >
                  $20 lifetime · 3 machines
                </span>
              </div>
              <h2 className="font-display mt-3 text-3xl lg:text-5xl font-bold tracking-tight max-w-2xl">
                Wire your terminals
                <br />
                <span className="text-cyan-accent">together.</span>
              </h2>
            </div>
            <p className="text-[var(--pg-text-dim)] text-sm max-w-md leading-relaxed">
              Pro is the difference between using terminals and{" "}
              <span className="text-white">composing</span> them. Broadcast
              one prompt to many panes. Pipe one pane's output into another's
              input. Hand the whole grid to your LLM. Each capability below
              comes with the reason it exists.
            </p>
          </div>

          <div
            data-testid="pro-features-grid"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4"
          >
            {PRO_FEATURES.map((f, i) => (
              <ProFeatureCell key={i} {...f} />
            ))}
          </div>
        </div>
      </section>

      {/* CALLOUT — MCP */}
      <section
        data-testid="mcp-callout-section"
        className="border-b border-pg bg-[#070707]"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="tag-label text-[var(--pg-orange)]">
              /// LLMs in the loop
            </div>
            <h2 className="font-display mt-3 text-3xl lg:text-5xl font-bold tracking-tight">
              Your terminals,
              <br />
              <span className="text-cyan-accent">visible</span> to your
              <br />
              models.
            </h2>
            <p className="mt-5 text-[var(--pg-text-dim)] leading-relaxed max-w-md">
              The Pro MCP server runs locally on 127.0.0.1. It exposes three
              tools — <code className="text-white">list_panes</code>,{" "}
              <code className="text-white">read_pane</code>,{" "}
              <code className="text-white">write_pane</code> — to Claude
              Desktop, Cursor, and Cline over JSON-RPC. 32-byte bearer token.
              Origin allowlist. Writable panes get a red inset border so you
              always know what's exposed.
            </p>
            <Button
              asChild
              variant="outline"
              data-testid="mcp-callout-cta"
              className="mt-6 h-11 rounded-sm border-pg bg-transparent hover:bg-pg-surface-2 font-display font-bold tag-label"
            >
              <Link to="/docs/mcp-server">
                MCP setup guide
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
          <div className="lg:col-span-7">
            <div className="bg-[#0a0a0a] border border-pg">
              <div className="flex items-center justify-between px-4 py-2 border-b border-pg bg-[#0d0d0d]">
                <span className="tag-label text-[var(--pg-text-dim)]">
                  ~/.config/claude/claude_desktop_config.json
                </span>
                <span className="tag-label text-[var(--pg-green)]">● live</span>
              </div>
              <pre className="p-5 text-[12px] md:text-[13px] leading-relaxed text-[var(--pg-text-dim)] font-mono overflow-x-auto">
{`{
  "mcpServers": {
    "polygrid": {
      "transport": "http",
      "url": "http://127.0.0.1:8473/mcp",
      "headers": {
        "Authorization": "Bearer <32-byte-token>"
      }
    }
  }
}`}
              </pre>
            </div>
          </div>
        </div>
      </section>

      {/* CALLOUT — 10x DEV GUIDE */}
      <section
        data-testid="ten-x-dev-section"
        className="border-b border-pg"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="tag-label text-cyan-accent">/// Power users</div>
            <h2 className="font-display mt-3 text-3xl lg:text-5xl font-bold tracking-tight">
              Become a <span className="text-cyan-accent">10x dev.</span>
            </h2>
            <p className="mt-5 text-[var(--pg-text-dim)] leading-relaxed max-w-xl">
              The grid gets you a "team of ten." The guide is what actually
              gets you there: capping context before it bleeds tokens,
              running separate spaces per context so nothing blocks, and
              keeping a queue fed instead of babysitting one pane at a time.
            </p>
            <Button
              asChild
              variant="outline"
              data-testid="ten-x-dev-cta"
              className="mt-6 h-11 rounded-sm border-pg bg-transparent hover:bg-pg-surface-2 font-display font-bold tag-label"
            >
              <Link to="/10x-dev">
                Read the power-user guide
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
          <div className="lg:col-span-5">
            <div className="border border-pg bg-pg-surface divide-y divide-[var(--pg-border)]">
              {[
                {
                  icon: Shrink,
                  label: "Auto-Compact",
                  body: "Cap context before a pane bleeds tokens re-reading its own history.",
                },
                {
                  icon: Layers,
                  label: "Multi-grid spaces",
                  body: "One space per context — nothing blocks on anything else.",
                },
                {
                  icon: Megaphone,
                  label: "Broadcast prompts",
                  body: "Type once, send to every pane working the same problem.",
                },
                {
                  icon: ListChecks,
                  label: "Keep-busy work queue",
                  body: "Queue the next prompt so an idle agent never sits waiting on you.",
                },
              ].map((item) => (
                <div
                  key={item.label}
                  className="flex items-start gap-3 p-4 md:p-5"
                >
                  <item.icon className="w-4 h-4 mt-0.5 text-cyan-accent shrink-0" strokeWidth={2} />
                  <div>
                    <div className="font-display font-bold text-sm">
                      {item.label}
                    </div>
                    <p className="mt-1 text-[13px] text-[var(--pg-text-dim)] leading-relaxed">
                      {item.body}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section
        id="pricing"
        data-testid="pricing-section"
        className="border-b border-pg"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="tag-label text-cyan-accent">/// Pricing</div>
            <h2 className="font-display mt-3 text-3xl lg:text-5xl font-bold tracking-tight">
              One price. Three machines. Forever.
            </h2>
            <p className="mt-4 text-[var(--pg-text-dim)] leading-relaxed">
              Try every Pro feature free for 7 days. After that, decide once.
              No renewals. No seat math.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-px bg-[var(--pg-border)] border border-pg max-w-4xl mx-auto">
            <PriceCard
              tier="Free"
              price="$0"
              priceNote="forever"
              testId="price-card-free"
              features={[
                "Multi-terminal grid (up to 9 panes)",
                "Real PTY with bash / zsh",
                "Compose bar + focus routing",
                "Default + Nord themes",
                "Session restore",
              ]}
              cta={heroLabel}
              ctaHref={primary.url}
              ctaExternal={primary.external}
              ctaTestId="price-cta-free"
              onCtaClick={() =>
                trackEvent("download_app", {
                  location: "pricing",
                  tier: "free",
                  platform: primary.platform,
                })
              }
            />
            <PriceCard
              tier="Pro"
              price="$20"
              priceNote="lifetime · 3 machines"
              testId="price-card-pro"
              highlight
              features={[
                "Everything in Free",
                "Broadcast prompts to pane groups",
                "Workspace presets + JSON export",
                "Cross-pane @mentions",
                "Per-pane macros (up to 5 + ⋯)",
                "Multi-grid spaces + git worktree panes",
                "Cross-pane scrollback search",
                "Local MCP server for Claude / Cursor / Cline",
                "Every v1.x update — free, forever",
              ]}
              cta="Start 7-day Pro trial"
              ctaHref={primary.url}
              ctaExternal={primary.external}
              ctaTestId="price-cta-pro"
              onCtaClick={() =>
                trackEvent("download_app", {
                  location: "pricing",
                  tier: "pro_trial",
                  platform: primary.platform,
                })
              }
            />
          </div>

          <div className="text-center mt-8 text-xs font-mono text-[var(--pg-text-muted)]">
            One-time payment. No subscription. Every v1.x update — free.
            30-day no-questions refund.
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section
        id="faq"
        data-testid="faq-section"
        className="border-b border-pg"
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="tag-label text-cyan-accent">/// FAQ</div>
          <h2 className="font-display mt-3 text-3xl lg:text-5xl font-bold tracking-tight mb-10">
            Common questions.
          </h2>

          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {FAQ_ITEMS.map((item, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="border-pg"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger className="font-display text-left text-lg font-bold hover:no-underline hover:text-cyan-accent transition-colors py-5">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-[var(--pg-text-dim)] leading-relaxed text-[15px] pb-6">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FINAL CTA */}
      <section
        data-testid="final-cta-section"
        className="relative overflow-hidden"
      >
        <div className="absolute inset-0 grid-bg opacity-50 pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 py-24 lg:py-32 text-center">
          <h2 className="font-display text-4xl sm:text-5xl lg:text-7xl font-black tracking-tighter uppercase leading-[0.95]">
            Build like a team.
            <br />
            <span className="text-cyan-accent">$ ./polygrid</span>
          </h2>
          <p className="mt-6 text-[var(--pg-text-dim)] max-w-xl mx-auto leading-relaxed">
            One developer, a grid of agents working in parallel. Tile the grid,
            broadcast one prompt to many panes, hand the whole thing to your LLM.
          </p>
          <p className="mt-3 text-xs font-mono text-[var(--pg-text-muted)] max-w-xl mx-auto">
            Free forever. 7-day Pro trial — every feature unlocked. $20 once for
            Pro: lifetime, 3 machines, every v1.x update free, 30-day refund.
          </p>
          <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
            <Button
              asChild
              data-testid="final-cta-download"
              className="h-12 px-7 rounded-sm font-display font-bold tag-label text-black hover:opacity-90"
              style={{ backgroundColor: "var(--pg-cyan)" }}
            >
              <a
                href={primary.url}
                download={!primary.external}
                target={primary.external ? "_blank" : undefined}
                rel={primary.external ? "noopener noreferrer" : undefined}
                onClick={() =>
                  trackEvent("download_app", {
                    location: "final_cta",
                    tier: "free",
                    platform: primary.platform,
                  })
                }
              >
                <Download className="w-4 h-4 mr-2" />
                {heroLabel}
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              data-testid="final-cta-docs"
              className="h-12 px-7 rounded-sm border-pg bg-transparent hover:bg-pg-surface-2 font-display font-bold tag-label"
            >
              <Link to="/docs">
                Read the docs
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>

          <OtherPlatforms
            others={others}
            location="final_cta"
            align="center"
          />
        </div>
      </section>

      <Footer />
    </div>
  );
};

const OtherPlatforms = ({ others, location, align = "left" }) => {
  if (!others || others.length === 0) return null;
  return (
    <div
      data-testid={`other-platforms-${location}`}
      className={`mt-4 text-[12px] font-mono text-[var(--pg-text-muted)] ${align === "center" ? "text-center" : ""}`}
    >
      <span>Also for: </span>
      {others.map((o, i) => (
        <span key={o.platform}>
          {i > 0 && <span className="mx-1.5 text-[var(--pg-border)]">·</span>}
          <a
            href={o.url}
            download={!o.external}
            target={o.external ? "_blank" : undefined}
            rel={o.external ? "noopener noreferrer" : undefined}
            data-testid={`other-platform-${location}-${o.platform}`}
            onClick={() =>
              trackEvent("download_app", {
                location: `${location}_alt`,
                tier: "free",
                platform: o.platform,
              })
            }
            className="text-[var(--pg-text-dim)] hover:text-cyan-accent underline-offset-4 hover:underline"
          >
            {o.label}
          </a>
        </span>
      ))}
    </div>
  );
};

const FeatureCell = ({ icon: Icon, title, body, pro, accent }) => (
  <div
    className="bg-pg-surface border border-pg p-6 md:p-7 group transition-all duration-200 hover:bg-pg-surface-2 hover:border-[var(--pg-cyan)] hover:-translate-y-0.5 hover:shadow-[0_8px_24px_-12px_rgba(62,232,122,0.3)]"
    data-testid={`feature-cell-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
  >
    <div
      className={`w-10 h-10 border border-pg flex items-center justify-center bg-[var(--pg-bg)] group-hover:border-[var(--pg-cyan)] transition-colors ${accent === "orange" ? "group-hover:border-[var(--pg-orange)]" : ""}`}
    >
      <Icon
        className={`w-5 h-5 ${accent === "orange" ? "text-[var(--pg-orange)]" : "text-cyan-accent"}`}
        strokeWidth={2}
      />
    </div>
    <div className="mt-5 flex items-center gap-2">
      <h3 className="font-display font-bold text-[17px] tracking-tight text-white">
        {title}
      </h3>
      {pro && (
        <span className="tag-label text-[10px] text-[var(--pg-cyan)] border border-pg px-1.5 py-0.5">
          PRO
        </span>
      )}
    </div>
    <p className="mt-2.5 text-[14px] text-[var(--pg-text-dim)] leading-relaxed">
      {body}
    </p>
  </div>
);

const ProFeatureCell = ({ icon: Icon, title, body, why, accent }) => {
  const isOrange = accent === "orange";
  return (
    <div
      className={`bg-[#0a0a0a] border border-pg p-7 md:p-8 group flex flex-col transition-all duration-200 hover:bg-pg-surface hover:-translate-y-0.5 ${isOrange ? "hover:border-[var(--pg-orange)] hover:shadow-[0_8px_24px_-12px_rgba(255,149,0,0.3)]" : "hover:border-[var(--pg-cyan)] hover:shadow-[0_8px_24px_-12px_rgba(62,232,122,0.3)]"}`}
      data-testid={`pro-feature-cell-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div
          className={`w-11 h-11 border border-pg flex items-center justify-center bg-[var(--pg-bg)] transition-colors ${isOrange ? "group-hover:border-[var(--pg-orange)]" : "group-hover:border-[var(--pg-cyan)]"}`}
        >
          <Icon
            className={`w-5 h-5 ${isOrange ? "text-[var(--pg-orange)]" : "text-cyan-accent"}`}
            strokeWidth={2}
          />
        </div>
        <span
          className="tag-label text-[10px] text-black px-1.5 py-0.5 font-bold"
          style={{ backgroundColor: isOrange ? "var(--pg-orange)" : "var(--pg-cyan)" }}
        >
          PRO
        </span>
      </div>

      <h3 className="mt-5 font-display font-bold text-[19px] tracking-tight text-white">
        {title}
      </h3>
      <p className="mt-2.5 text-[14px] text-[var(--pg-text-dim)] leading-relaxed">
        {body}
      </p>

      <div className="mt-5 pt-5 border-t border-dashed border-pg">
        <div className="flex items-center gap-2 mb-2">
          <span
            className="inline-block w-1.5 h-1.5"
            style={{ backgroundColor: isOrange ? "var(--pg-orange)" : "var(--pg-cyan)" }}
          />
          <span
            className="tag-label"
            style={{ color: isOrange ? "var(--pg-orange)" : "var(--pg-cyan)" }}
          >
            Why it's pro
          </span>
        </div>
        <p className="text-[13.5px] text-white/85 leading-relaxed">{why}</p>
      </div>
    </div>
  );
};

const PriceCard = ({
  tier,
  price,
  priceNote,
  features,
  cta,
  ctaHref,
  ctaExternal,
  onCtaClick,
  highlight,
  testId,
  ctaTestId,
}) => (
  <div
    data-testid={testId}
    className={`bg-[var(--pg-bg)] p-8 md:p-10 flex flex-col ${highlight ? "relative" : ""}`}
  >
    {highlight && (
      <div className="absolute top-0 right-0 bg-cyan-accent text-black tag-label px-3 py-1"
        style={{ backgroundColor: "var(--pg-cyan)" }}
      >
        Recommended
      </div>
    )}
    <div className="tag-label text-[var(--pg-text-muted)]">{tier}</div>
    <div className="mt-4 flex items-baseline gap-2">
      <span className="font-display text-5xl lg:text-6xl font-black tracking-tighter">
        {price}
      </span>
      <span className="text-xs font-mono text-[var(--pg-text-dim)]">
        {priceNote}
      </span>
    </div>
    <ul className="mt-7 space-y-3 text-[14px] flex-1">
      {features.map((f, i) => (
        <li key={i} className="flex items-start gap-2.5">
          <Check
            className={`w-4 h-4 mt-0.5 shrink-0 ${highlight ? "text-cyan-accent" : "text-[var(--pg-text-dim)]"}`}
            strokeWidth={2.5}
          />
          <span className="text-[var(--pg-text-dim)]">{f}</span>
        </li>
      ))}
    </ul>
    <Button
      asChild
      data-testid={ctaTestId}
      className={`mt-8 h-11 rounded-sm font-display font-bold tag-label ${highlight ? "text-black hover:opacity-90" : "bg-transparent border border-pg text-white hover:bg-pg-surface-2"}`}
      style={highlight ? { backgroundColor: "var(--pg-cyan)" } : {}}
    >
      <a
        href={ctaHref}
        download={!ctaExternal}
        target={ctaExternal ? "_blank" : undefined}
        rel={ctaExternal ? "noopener noreferrer" : undefined}
        onClick={onCtaClick}
      >
        {cta}
        <ArrowRight className="w-4 h-4 ml-2" />
      </a>
    </Button>
  </div>
);

const FAQ_ITEMS = [
  {
    q: "How does the 7-day trial work?",
    a: "The trial starts on first launch and runs for 7 days of real-world time. Every Pro feature is fully enabled — broadcast, presets, @mentions, macros, MCP. When the trial ends, locked features open the activation modal instead of running. The free tier keeps working forever.",
  },
  {
    q: "What's the license model?",
    a: "$20 one-time for 3 machine activations. Lifetime, no subscription. Every v1.x update is free — you pay once and keep getting new features. You can deactivate a machine at any time to free up a slot. 30-day no-questions refund.",
  },
  {
    q: "Do I get future updates?",
    a: "Yes. Your one-time $20 payment includes every v1.x release — new features, performance work, bug fixes — free, forever. Major version upgrades (v2.x, v3.x) aren't guaranteed to be free, but if we ever ship a paid major version, existing license holders get a meaningful discount or grandfathering window.",
  },
  {
    q: "Does it work on Apple Silicon and Intel?",
    a: "Yes. The .dmg is a universal binary. macOS 12 or later is required.",
  },
  {
    q: "What shell does the terminal use?",
    a: "Your default login shell — bash, zsh, fish — whatever you've set. Poly Grid spawns a full PTY with xterm-256color and a 5,000-line scrollback per pane.",
  },
  {
    q: "Is the MCP server safe to expose to LLMs?",
    a: "It binds to 127.0.0.1 only, requires a 32-byte bearer token, and enforces an origin allowlist. Each pane has explicit readable/writable opt-in — writable panes get a red inset border so you can always see what's exposed.",
  },
  {
    q: "Can I use the webserver from outside my network?",
    a: "Yes. The webserver binds to your LAN, so anything that can reach your machine can serve it — point Tailscale, a Cloudflare tunnel, or any reverse proxy at the port and you can use it from anywhere. It's a terminal, so treat it like one: turn on the login password before you expose it beyond your own Wi-Fi, and let the tunnel handle TLS.",
  },
  {
    q: "Is Sentinel watching everything I type?",
    a: "Sentinel is opt-in and only ever emits events — it never sends keystrokes or touches your agents. It reads pane output to classify state and write summaries. Decline it and the app runs exactly as before. The rules that flag risky commands are deterministic and run with no model at all.",
  },
  {
    q: "Do I need to set up a model for Sentinel?",
    a: "For the summaries and state classifications, yes — you supply the model. Point Sentinel at a local OpenAI-compatible endpoint (Ollama, LM Studio, anything that speaks the API) or drop in your own API key. The deterministic rules that flag risky commands need no model at all, so that layer works the moment you turn Sentinel on.",
  },
  {
    q: "Can I export my workspace presets?",
    a: "Yes. Single-preset and whole-library JSON export and import are built in. Share a stack with a teammate, version-control your terminal layouts.",
  },
  {
    q: "Linux and Windows builds?",
    a: (
      <div className="space-y-4">
        <p>
          macOS only at launch. Linux and Windows are on the roadmap but not
          committed to a date. Leave your email and we'll send you one
          message when your build ships. Nothing else.
        </p>
        <WaitlistForm location="faq" />
      </div>
    ),
  },
];

export default Landing;
